import React from "react";
import { Alert, Box, Button, CircularProgress, LinearProgress, Typography } from "@mui/material";
import CheckIcon from "@mui/icons-material/Check";
import CloseIcon from "@mui/icons-material/Close";
import DescriptionIcon from "@mui/icons-material/Description";
import ReplayIcon from "@mui/icons-material/Replay";
import {
  REPORT_PANEL_HEADINGS,
  REPORT_RUNNING_REASSURANCE,
  ReportPhase,
  ReportStep,
} from "./reportSteps";

export interface ReportProgressPanelProps {
  steps: ReportStep[];
  activeIndex: number;
  phase: ReportPhase;
  // By step key. A finished step carries its final duration; the active step's value is
  // refreshed every second by the caller.
  stepElapsedMs?: Record<string, number>;
  totalElapsedMs?: number;
  // Failure or ceiling copy. Absent while running.
  message?: string;
  isAdmin: boolean;
  actionBusyId?: string | null;
  actionError?: string | null;
  onAction?: (actionId: string) => void;
  onRetry?: () => void;
}

type RowState = "pending" | "active" | "done" | "failed";

// m:ss, counting UP. Never an estimate, never a percentage: measured cold transcription ran 51 s
// to 467 s with no correlation to recording length, so any prediction would be wrong often
// enough to cost trust.
const formatElapsed = (ms: number): string => {
  const totalSeconds = Math.max(0, Math.round(ms / 1000));
  const minutes = Math.floor(totalSeconds / 60);
  return `${minutes}:${String(totalSeconds % 60).padStart(2, "0")}`;
};

// The row state machine. Three decisions live here that the code alone does not explain:
//   1. 'done' outranks the index entirely, so a finished job paints EVERY row green - including
//      rows the poll never visited, and including activeIndex -1.
//   2. Only the row that was running can fail. Rows before it stay done, rows after stay pending,
//      which is what lets a stage-2 failure keep row 1 green with a working transcript button.
//   3. The last line is the ONLY thing keeping 'detached' from rendering red. Detached means the
//      client ceiling tripped and we stopped watching; the job is probably still alive in Node.
//      Falling through to 'active' is deliberate - see reportSteps.ts's ReportPhase comment.
// A failed row keeps its `active` label, so it reads in the present continuous. That is spec, not
// an oversight: REPORT_STEPS defines three labels and none of them is a past-tense failure.
const rowStateFor = (index: number, activeIndex: number, phase: ReportPhase): RowState => {
  if (phase === "done") return "done";
  if (index < activeIndex) return "done";
  if (index > activeIndex) return "pending";
  return phase === "failed" ? "failed" : "active";
};

// Exactly ONE icon is rendered per state. Putting all four inside the node and hiding three
// with CSS is where the stacked-icon defect came from - do not reintroduce that shape.
// `frozen` is the ceiling case: the row keeps its active treatment but stops animating,
// because nothing is being observed any more. Its value={70} is a static arc chosen to read as
// "stopped mid-work" - it is NOT a progress reading, which this file forbids everywhere else.
const StepNode: React.FC<{ state: RowState; frozen: boolean }> = ({ state, frozen }) => {
  const shell = {
    width: 32,
    height: 32,
    borderRadius: "50%",
    display: "grid",
    placeItems: "center",
    flex: "none",
  } as const;

  if (state === "done") {
    return (
      <Box sx={{ ...shell, bgcolor: "success.main", border: 2, borderColor: "success.main" }}>
        {/* contrastText, NOT common.white. Dark mode fills this node with a light success.main,
            against which white measures 2.36:1 - under WCAG 1.4.11's 3:1 floor for non-text UI.
            The theme already declares the right foreground there, and it composites to 7.68:1.
            Light mode was passing only because its success.contrastText happens to equal white:
            a palette path that resolves the same in both modes is a hardcoded colour in disguise. */}
        <CheckIcon sx={{ fontSize: 18, color: "success.contrastText" }} />
      </Box>
    );
  }

  if (state === "failed") {
    return (
      <Box sx={{ ...shell, bgcolor: "error.main", border: 2, borderColor: "error.main" }}>
        {/* Passes on white in both themes today, but by luck rather than construction. Tracking
            error.contrastText keeps it correct if the error palette is ever retuned. */}
        <CloseIcon sx={{ fontSize: 18, color: "error.contrastText" }} />
      </Box>
    );
  }

  if (state === "active") {
    return (
      <Box sx={shell}>
        <CircularProgress
          size={26}
          thickness={4}
          variant={frozen ? "determinate" : "indeterminate"}
          value={frozen ? 70 : undefined}
          sx={frozen ? { color: "text.disabled" } : undefined}
        />
      </Box>
    );
  }

  return (
    <Box sx={{ ...shell, border: 2, borderColor: "divider", bgcolor: "background.paper" }}>
      <Box sx={{ width: 9, height: 9, borderRadius: "50%", bgcolor: "text.disabled" }} />
    </Box>
  );
};

// A measured duration OUTRANKS authored copy: once a step has a timing, "Finished in 1:12" replaces
// captions.done. That is why the terminal row's "Opening now" survives - nothing ever measures it.
// Add a timing for that row and the authored caption disappears. A failed row with no timing is left
// deliberately bare rather than captioned, because the failure message renders below the whole list.
const captionFor = (step: ReportStep, state: RowState, elapsedMs?: number): string => {
  if (state === "done") {
    return typeof elapsedMs === "number"
      ? `Finished in ${formatElapsed(elapsedMs)}`
      : step.captions?.done ?? "";
  }
  if (state === "failed") {
    return typeof elapsedMs === "number" ? `Stopped after ${formatElapsed(elapsedMs)}` : "";
  }
  if (state === "active") {
    const base = step.captions?.active ?? "";
    if (typeof elapsedMs !== "number") return base;
    return base ? `${base} · ${formatElapsed(elapsedMs)}` : formatElapsed(elapsedMs);
  }
  return step.captions?.pending ?? "";
};

const labelColorFor = (state: RowState): string => {
  if (state === "active") return "primary.main";
  if (state === "failed") return "error.main";
  if (state === "pending") return "text.disabled";
  return "text.primary";
};

const ReportProgressPanel: React.FC<ReportProgressPanelProps> = ({
  steps,
  activeIndex,
  phase,
  stepElapsedMs,
  totalElapsedMs,
  message,
  isAdmin,
  actionBusyId,
  actionError,
  onAction,
  onRetry,
}) => {
  const isRunning = phase === "running";

  return (
    <Box py={1}>
      {/* Indeterminate bar, running only. On a terminal phase it would animate work that has
          stopped. */}
      <Box sx={{ height: 3, mb: 2 }}>
        {isRunning && <LinearProgress sx={{ height: 3, borderRadius: 2 }} />}
      </Box>

      <Box display="flex" justifyContent="space-between" alignItems="baseline" mb={2}>
        <Typography variant="subtitle1" fontWeight={600}>
          {REPORT_PANEL_HEADINGS[phase]}
        </Typography>
        {typeof totalElapsedMs === "number" && (
          <Typography variant="body2" color="text.secondary" sx={{ fontVariantNumeric: "tabular-nums" }}>
            {phase === "done"
              ? `${formatElapsed(totalElapsedMs)} total`
              : `${formatElapsed(totalElapsedMs)} elapsed`}
          </Typography>
        )}
      </Box>

      <Box component="ol" sx={{ listStyle: "none", m: 0, p: 0 }}>
        {steps.map((step, index) => {
          const state = rowStateFor(index, activeIndex, phase);
          const elapsedMs = stepElapsedMs?.[step.key];
          const caption = captionFor(step, state, elapsedMs);
          const showAction =
            state === "done" && !!step.action && (!step.action.requiresAdmin || isAdmin);
          const showRetry = state === "failed" && !!onRetry;

          return (
            <Box
              component="li"
              key={step.key}
              sx={{
                display: "grid",
                gridTemplateColumns: "32px 1fr auto",
                gap: 2,
                alignItems: "flex-start",
                // Theme multipliers, not raw px: 1.25/1.5/1 emit 10/12/8px on the 8px scale.
                pt: 1.25,
                pr: 1.5,
                pb: 1.25,
                pl: 1,
                borderRadius: 2,
                position: "relative",
                bgcolor: state === "active" ? "action.hover" : "transparent",
                // The connector between nodes. Green once the step above is done, so the rail
                // reads as a filled track rather than a list of dots.
                // These three ARE raw px, and must stay raw: the geometry is pinned to the node,
                // not chosen. left 23 = 8px left padding + 16px half-node - 1px half-connector, so
                // a 2px line centres exactly on the 24px node axis. top 44 = 10px padding + 32px
                // node + 2px clearance. Snapping any of them to the 8px scale decentres the rail.
                "&:not(:last-of-type)::after": {
                  content: '""',
                  position: "absolute",
                  left: "23px",
                  top: "44px",
                  bottom: "-6px",
                  width: "2px",
                  borderRadius: "1px",
                  bgcolor: state === "done" ? "success.light" : "divider",
                },
              }}
            >
              <StepNode state={state} frozen={phase === "detached"} />

              <Box>
                <Typography
                  variant="body1"
                  fontWeight={state === "pending" ? 500 : 600}
                  sx={{ color: labelColorFor(state), lineHeight: 1.35 }}
                >
                  {state === "done"
                    ? step.labels.done
                    : state === "pending"
                    ? step.labels.pending
                    : step.labels.active}
                </Typography>
                {caption && (
                  <Typography
                    variant="body2"
                    sx={{
                      mt: 0.375,
                      color: state === "failed" ? "error.main" : "text.secondary",
                      fontVariantNumeric: "tabular-nums",
                    }}
                  >
                    {caption}
                  </Typography>
                )}
              </Box>

              <Box alignSelf="center">
                {showAction && step.action && (
                  <Button
                    size="small"
                    color="success"
                    variant="outlined"
                    // "999px" as a string, because a NUMBER here is multiplied by
                    // theme.shape.borderRadius (4) - a plain-CSS 18px would become 72px.
                    // Either way the browser clamps to a pill; the string says so honestly.
                    sx={{ borderRadius: "999px", textTransform: "none" }}
                    disabled={actionBusyId === step.action.id}
                    startIcon={
                      actionBusyId === step.action.id ? (
                        <CircularProgress size={14} />
                      ) : (
                        <DescriptionIcon />
                      )
                    }
                    onClick={() => onAction?.(step.action!.id)}
                  >
                    {step.action.label}
                  </Button>
                )}
                {showRetry && (
                  <Button
                    size="small"
                    color="error"
                    variant="outlined"
                    sx={{ borderRadius: "999px", textTransform: "none" }}
                    startIcon={<ReplayIcon />}
                    onClick={onRetry}
                  >
                    Try again
                  </Button>
                )}
              </Box>
            </Box>
          );
        })}
      </Box>

      {/* A row action failing must never take over the modal body - that would delete the step
          list mid-job. It reports here instead. */}
      {actionError && (
        <Typography variant="body2" color="error" sx={{ mt: 1.5 }}>
          {actionError}
        </Typography>
      )}

      {message && (
        <Alert severity={phase === "failed" ? "error" : "info"} sx={{ mt: 2 }}>
          {message}
        </Alert>
      )}

      {isRunning && (
        <Typography
          variant="body2"
          color="text.secondary"
          textAlign="center"
          // A dashed RULE above the line, not a box around it. `borderStyle` is a four-sided
          // property, so pairing it with `borderTop: 1` styles all four edges while only the top
          // has a declared width - the other three fall back to the CSS initial `medium` and draw
          // a 3px dashed box. Keep width, style and side in one `borderTop` value so no other edge
          // can pick up a style.
          sx={{ mt: 2.5, pt: 2, borderTop: "1px dashed", borderColor: "divider" }}
        >
          {REPORT_RUNNING_REASSURANCE}
        </Typography>
      )}
    </Box>
  );
};

export default ReportProgressPanel;
